import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	useTheme,
	createMuiTheme,
	ThemeProvider,
} from '@material-ui/core/';
import { ModalSubmitProps, debounce, genericOnSubmit, onSubmitProps, imageDimensions as dimensions } from '../../Constants/constants';
import ConfirmModal, { IConfirmModal } from './ConfirmModal';
import React, { ReactElement, useContext, useState } from 'react';

import { AppContext } from '../../Context/AppContext';
import { useForm, FormContext } from 'react-hook-form';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ModalFileUpload, { IDimensions } from './ModalFileUpload';
import FileServiceAPI from '../../API/Services/FileServiceAPI';
import { DictImageType } from '../../API/Interfaces/Enums';
import { Helper } from '../../Utils';

export interface IModalProps<T> {
	entity: T;
	entityName?: string;
	callbacks: onSubmitProps;
	isOpen: boolean;
	fullscreen?: boolean;
	form: ReactElement | null;
	sendDataToAPI?: boolean;
	modalTitle?: any;
	modalSubtitle?: string;
	blueTheme?: boolean;
	titleComponent?: ReactElement;
	addButton?: ReactElement;
	onlyCancelButton?: boolean;
	cancelButtonText?: string;
	warningCondition?: (v: any) => boolean;
	warningMessage?: string;
	breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
	padding?: string;
	withFileUpload?: boolean;
	withAdditionalFileUpload?: boolean;
	fileLabel?: string;
	additionalFileLabel?: string;
	imageType?: DictImageType;
	closeAfterUploadImage?: boolean;
	imageDimensions?: IDimensions;
	isReadOnly?: boolean;
}

const blueMuiTheme = createMuiTheme({ palette: { primary: { main: '#1045FF' } } });

export default function Modal(props: IModalProps<any>) {
	const { t } = useTranslation();
	const {
		form,
		entity,
		callbacks,
		entityName,
		isOpen,
		sendDataToAPI = true,
		modalTitle,
		modalSubtitle,
		blueTheme,
		titleComponent,
		onlyCancelButton,
		addButton,
		cancelButtonText,
		breakpoint,
		padding,
		warningCondition,
		withFileUpload,
		withAdditionalFileUpload,
		fileLabel,
		additionalFileLabel,
		imageType,
		closeAfterUploadImage,
		imageDimensions,
		isReadOnly,
	} = props;
	const theme = useTheme();
	const { enqueueSnackbar } = useSnackbar();
	const appContext = useContext(AppContext);
	const mediaQueryFullscreen = useMediaQuery(theme.breakpoints.down(breakpoint || 'xs'));
	const fullScreen = props.fullscreen ? true : mediaQueryFullscreen;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [Picture, setPicture] = useState<File | null>();
	const [AdditionalPicture, setAdditionalPicture] = useState<File | null>();

	const methods = useForm({
		defaultValues: entity,
		validateCriteriaMode: 'all',
		submitFocusError: true,
		mode: 'onBlur',
		reValidateMode: 'onChange',
	});

	const { handleSubmit, errors } = methods;

	const submitProps: ModalSubmitProps = {
		useSnackbar: enqueueSnackbar,
		useTranslation: t,
		appContext: appContext,

		presetDataCallback: callbacks.presetDataCallback,
		setModalOpen: callbacks.setModalOpen,
		api: callbacks.api,
		setId: callbacks.setId,
		setEntity: callbacks.setEntity,
		setOnSuccessSubmit: callbacks.setOnSuccessSubmit,
		refreshTable: callbacks.refreshTable,
	};

	const addFile = async (data: any, additional = false) => {
		Helper.setLoader(true);
		let request = {
			data: {
				file: additional ? AdditionalPicture! : Picture!,
				name: data.name,
				corporationId: data.corporationId,
				id: additional ? data.statsImageId : data.imageId,
			},
			imageType,
		};
		let response;
		if (data.statsImageId || data.imageId) {
			response = await FileServiceAPI.Update(request.data, request.imageType!);
		} else response = await FileServiceAPI.Add(request.data, request.imageType!);
		if (response.errors) {
			Helper.setLoader(false);
			let errorMessage = t('common.errorOccured');
			const errorCode = response.errors && response.errors[0]?.errorCode;
			if (errorCode) errorMessage = errorMessage + ': ' + t(`errorCodes.${response.errors[0].errorCode}`);
			enqueueSnackbar(errorMessage, { variant: 'error' });
		} else {
			if (closeAfterUploadImage) {
				Helper.setLoader(false);
				callbacks.setModalOpen(false);
				enqueueSnackbar(t('common.dataSaved'), { variant: 'success' });
				callbacks.refreshTable && callbacks.refreshTable();
			} else return response.data;
		}
	};

	const onSubmit = (data: any) => {
		if (errors && Object.keys(errors).length === 0 && Object.getPrototypeOf(errors) === Object.prototype) {
			if (data.phone) data.phone = data.phone.replace(/\s/g, '');
			if (data.deleteStatsFile === 'true') data.statsImageId = null;

			if (sendDataToAPI === false) {
				if (callbacks.presetDataCallback) data = callbacks.presetDataCallback(data);

				if (callbacks.setEntity) callbacks.setEntity(data);

				if (callbacks.setOnSaveClickHandler) callbacks.setOnSaveClickHandler(data);
			} else genericOnSubmit(data, submitProps);
		} else {
			return;
		}
	};

	const presetImage = async (data: any) => {
		if (errors && Object.keys(errors).length === 0 && Object.getPrototypeOf(errors) === Object.prototype) {
			if ((withFileUpload && Picture) || (withAdditionalFileUpload && AdditionalPicture)) {
				const imageId = Picture ? await addFile(data) : data.imageId;
				const statsImageId = AdditionalPicture ? await addFile(data, true) : data.statsImageId;

				data.imageId = imageId;
				data.statsImageId = statsImageId;
				imageId && (statsImageId || AdditionalPicture == null) && onSubmit(data);
			} else {
				onSubmit(data);
			}
		} else {
			return;
		}
	};

	const onConfirm = (data: any) => {
		if (
			(warningCondition && warningCondition(data) === true) ||
			(((data.deleteStatsFile === 'true' && data.statsImageId) || (!data.statsImageId && !AdditionalPicture)) &&
				withAdditionalFileUpload)
		)
			setIsConfirmModalOpen(true);
		else presetImage(data);
	};

	const handleConfirmationModalProps: IConfirmModal = {
		handleOperation: handleSubmit(presetImage),
		isOpen: isConfirmModalOpen,
		handleClose: () => setIsConfirmModalOpen(false),
		text: props.warningMessage ?? '',
		variant: 'outlined',
	};

	const title = `${entity?.id == null || entity == null ? t('common.add') : t('common.update')} ${entityName}`;

	return (
		<>
			{console.log(isReadOnly)}
			{isConfirmModalOpen && <ConfirmModal {...handleConfirmationModalProps} />}
			<Dialog fullScreen={fullScreen} maxWidth={'md'} open={isOpen} onClose={() => callbacks.setModalOpen(false)}>
				<DialogTitle>
					{modalTitle ? (
						<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
							<div style={{ flexDirection: 'column' }}>
								<div style={{ display: 'flex' }}>
									{modalTitle}
									{addButton && addButton}
								</div>
								<Grid className='info-box-content-item-label'>{modalSubtitle}</Grid>
							</div>
							{titleComponent && (
								<FormContext {...methods}>
									<FormControl>{titleComponent}</FormControl>
								</FormContext>
							)}
						</div>
					) : (
						title
					)}
				</DialogTitle>
				<DialogContent style={{ padding: padding ?? '10px 24px 44px' }}>
					<FormContext {...methods}>
						{' '}
						<FormControl>
							<Grid container spacing={3}>
								{form}
								{withFileUpload && (
									<ModalFileUpload
										setPicture={setPicture}
										methods={methods}
										title={fileLabel ?? ''}
										imageDimensions={imageDimensions ?? { width: 0, height: 0 }}
										isReadOnly={!!isReadOnly}
									/>
								)}
								{withAdditionalFileUpload && (
									<ModalFileUpload
										setPicture={setAdditionalPicture}
										methods={methods}
										isAdditional
										title={additionalFileLabel ?? ''}
										imageDimensions={dimensions.additionalGoalImage}
										isReadOnly={!!isReadOnly}
									/>
								)}
							</Grid>
						</FormControl>
					</FormContext>
				</DialogContent>
				<DialogActions>
					{blueTheme ? (
						<ThemeProvider theme={blueMuiTheme}>
							<Button onClick={() => callbacks.setModalOpen(false)} color='primary' variant='contained'>
								{t('common.finish')}
							</Button>
						</ThemeProvider>
					) : (
						<>
							<Button onClick={() => callbacks.setModalOpen(false)} color='primary' variant='outlined'>
								{onlyCancelButton ? (cancelButtonText ? cancelButtonText : t('common.finish')) : t('common.cancel')}
							</Button>
							{!onlyCancelButton && (
								<Button onClick={debounce(handleSubmit(onConfirm), 5000, true)} color='primary' variant='outlined'>
									{' '}
									{t('common.save')}
								</Button>
							)}
						</>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
}
